---
title: "Barcode scanner not working"
slug: "barcode-scanner-not-working"
category: "troubleshooting"
description: "A diagnostic walk-through for when the scanner beeps but BookBurrow doesn't see anything."
tags: ["scanner", "barcode", "broken", "troubleshooting", "usb"]
order: 1
---

If BookBurrow isn't reading your barcodes, nine times out of ten it's one of three things: the scanner isn't in "keyboard wedge" mode, the scan field isn't focused, or the USB connection is flaky. This guide walks through each in order, starting with the most likely.

If you're completely new to scanners, it's worth skimming [Scanning books and ISBN auto-lookup](/help/guides/scanning-books-isbn-lookup) first — it explains what BookBurrow expects from a scanner in the first place, which is enough to resolve many "issues" that turn out not to be issues at all.

## First: does the scanner beep at all?

Point the scanner at a barcode and pull the trigger.

- **No beep, no red line** — the scanner isn't turning on. Check the USB cable is fully seated in both ends, and try a different USB port (preferably one directly on the computer, not a hub).
- **Red line but no beep** — some scanners are silent by default. Check the scanner manual for a "beep on successful scan" setting; usually it's a barcode in the manual that you scan to toggle beep.
- **Beep but nothing happens in BookBurrow** — the scanner is working; skip to the next section.

## Check #1 — Keyboard wedge mode

USB barcode scanners come in two modes:

- **Keyboard wedge** (also called HID or "keyboard emulation") — the scanner acts like a tiny keyboard, typing the barcode number at whatever field is focused. **This is the mode BookBurrow needs.**
- **Serial / COM** mode — the scanner sends data as if it were an old-school serial device. BookBurrow doesn't use this.

Most cheap USB scanners ship in keyboard wedge mode by default, but some need to be told. Find the scanner's printed manual (or a PDF from the manufacturer's site) and scan the **"Set to HID keyboard"** or **"Set to keyboard emulation"** barcode. You'll hear a confirmation beep.

<Callout variant="tip" title="Confirming keyboard wedge mode">
Open any text editor or even the URL bar of a browser, click so the cursor is in the text field, and scan a barcode. If the number appears in the text field, you're in keyboard wedge mode and the scanner is fine — the issue is elsewhere.
</Callout>

## Check #2 — Is the scan field focused in BookBurrow?

BookBurrow's Lend and Return screens focus the scan input automatically when you open them. But if you've clicked somewhere else — say, the sidebar, or a different tab — the cursor may have moved and the scan will go into the void.

Click once in the scan field so the cursor is flashing there, then try scanning. If it works, the fix is to just remember to click first. BookBurrow will also refocus the scan field automatically any time the screen is idle for more than a second.

## Check #3 — Is the scanner adding unexpected characters?

Some scanners add a carriage return (Enter), a tab, or other characters after every scan. BookBurrow expects **just the digits + Enter**. If your scanner is configured differently:

- **Extra Tab after scan** — BookBurrow won't recognise the number. Remove the tab setting from the scanner's config (another barcode in the manual).
- **No Enter after scan** — BookBurrow won't know the scan is finished. Add an "Auto-Enter" setting to the scanner.
- **Prefix characters** — some scanners insert a character at the start of every scan. Remove any prefix from the scanner's config.

## Check #4 — Language / keyboard layout mismatch

Windows machines default to a keyboard layout (e.g. **en-GB**). Some scanners default to **en-US**. On a UK keyboard, this can turn a digit into a punctuation character. Symptoms: scans produce garbled text instead of numbers.

Fix: scan the scanner's "Set to UK keyboard layout" configuration barcode (in its manual). Alternatively, change the scanner to "Alt code" mode, which works regardless of Windows keyboard layout.

## Check #5 — Damaged or worn barcode

If one specific barcode doesn't scan but others do, the issue is the barcode itself:

- **Reflective label**: gloss labels sometimes confuse a scanner. Angle the scanner at about 20 degrees instead of straight on.
- **Damaged barcode**: scuffed, scratched, or bent. Reprint the label.
- **Wrong symbology**: BookBurrow reads Code 39, Code 128, EAN-13, and EAN-8. Anything exotic (QR codes, for example) won't scan by default.

## Check #6 — USB cable or port

Old, cheap USB cables can deliver enough power for the scanner to turn on but not enough for reliable data. Symptoms: works on one port, not another; sometimes scans, sometimes doesn't.

Fix: try a different USB cable, and use a port directly on the computer (not through a hub). A USB 2.0 port is perfectly fine — 3.0 adds no benefit for a barcode scanner.

## Still stuck?

If none of the above helps, email support@bookburrow.uk with:

- The make and model of the scanner.
- What happens when you scan into a plain text editor (does the number appear?).
- What happens when you scan into BookBurrow's Lend screen.

We've seen nearly every scanner that's sold in the UK and can usually point you straight at the fix.

If the scanner is working on other computers but not on yours, the next thing to check is that BookBurrow is actually installed and running as expected — occasionally a scanner problem turns out to be an [installation problem](/help/guides/installing-bookburrow) in disguise.
